import { isValidArray } from "../../Services/validators";
import { getLocationList } from "./Complaints";
import {
  alignPhoneNumber,
  dateAndTimeConverter,
  isValidDate
} from "./Constants";

export const workforceTableHeadings = [
  { title: "SI.No", width: "50px", textAlign: "left" },
  { title: "NAME", width: "180px", textAlign: "left" },
  { title: "TYPE", width: "180px", textAlign: "left" },
  {
    title: "CONTACT",
    width: "180px",
    textAlign: "left",
    textTransform: "lowercase"
  },
  { title: "CAPTURED AT", width: "180px", textAlign: "right" }
];

export const workforceQueryPath = {
  role: "roles",
  captureApproval: "captureApproval"
};

export const captureQueryPath = {
  role: "employee.roles",
  dateRange: "createdAt"
};

export const attendanceQueryPath = {
  dateRange: "timestamp"
};

export const defaultWorkforceFilter = {
  assignedLocations: [],
  type: []
};

export function workforceTableField(data, type) {
  switch (data) {
    case "SI.No":
      return "serialNo";
    case "NAME":
      return "name";
    case "TYPE":
      return "type";
    case "CONTACT":
      return "phoneNumber";
    case "CAPTURED AT":
      return type === "sort" ? "captureStatus.lastCapturedAt" : "capturedAt";
    default:
      return data?.toLowerCase();
  }
}

export function convertWorkforceToTableData(data) {
  const workforceTableData = [];

  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    const transformedItem = {
      ...item,
      serialNo: i + 1,
      documentId: item?.documentId,
      name: item?.name,
      phoneNumber: item?.email || alignPhoneNumber(item?.phoneNumber),
      type: item?.type,
      capturedAt: item?.captureStatus?.lastCapturedAt
        ? `${dateAndTimeConverter(
            item?.captureStatus?.lastCapturedAt,
            "Time"
          )}, ${dateAndTimeConverter(
            item?.captureStatus?.lastCapturedAt,
            "thirdDate"
          )}, PCT : ${item?.captureStatus?.location?.PCTNo}`
        : "N/A"
    };
    workforceTableData.push(transformedItem);
  }
  return workforceTableData;
}

export const workforceQueryBuilder = (filterData, roles) => {
  const query = [];

  const addQuery = (path, operator, value) => {
    query.push(path, operator, value);
  };

  Object.entries(filterData).forEach(([key, value]) => {
    if (!value) {
      return;
    }

    switch (key) {
      case "role":
        if (isValidArray(value)) {
          addQuery(
            workforceQueryPath[key],
            "array-contains-any",
            Object.values(roles)
              .map((role) => value.includes(role.name) && role.documentId)
              .filter(Boolean)
          );
        }
        break;

      case "captureApproval":
        if (isValidArray(value)) {
          addQuery(workforceQueryPath[key], "==", false);
        }
        break;

      // archived status is applied client-side only
      case "status":
        break;

      default:
        if (isValidArray(value)) {
          addQuery(workforceQueryPath[key], "in", value);
        }
        break;
    }
  });

  return query;
};

export const capturesQueryBuilder = (filterData, roles) => {
  const query = [];

  const addQuery = (path, operator, value) => {
    query.push(path, operator, value);
  };

  const addRangeQuery = (path, from, to) => {
    addQuery(path, ">=", +new Date(`${from}:00:00:00`));
    addQuery(path, "<=", +new Date(`${to}:23:59:59`));
  };

  Object.entries(filterData).forEach(([key, value]) => {
    if (!value) {
      return;
    }

    switch (key) {
      case "dateRange":
        if (
          value.from &&
          isValidDate(value.from) &&
          value.to &&
          isValidDate(value.to)
        ) {
          const fromDate = new Date(value.from);
          const toDate = new Date(value.to);
          const diffInMs = toDate - fromDate;
          const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

          if (diffInDays <= 30) {
            addRangeQuery(captureQueryPath[key], value.from, value.to);
          } else {
            console.warn("Date range exceeds 30 days, skipping query.");
          }
        }
        break;

      case "role":
        if (isValidArray(value)) {
          addQuery(
            captureQueryPath[key],
            "array-contains-any",
            Object.values(roles)
              .map((role) => value.includes(role.name) && role.documentId)
              .filter(Boolean)
          );
        }
        break;

      case "captureApproval":
      case "status":
        break;

      default:
        if (isValidArray(value)) {
          addQuery(captureQueryPath[key], "in", value);
        }
        break;
    }
  });

  return query;
};

export const attendanceQueryBuilder = (filterData) => {
  const query = [];

  const addQuery = (path, operator, value) => {
    query.push(path, operator, value);
  };

  const addRangeQuery = (path, from, to) => {
    addQuery(path, ">=", +new Date(`${from}:00:00:00`));
    addQuery(path, "<=", +new Date(`${to}:23:59:59`));
  };

  Object.entries(filterData).forEach(([key, value]) => {
    if (!value) {
      return;
    }

    switch (key) {
      case "dateRange":
        if (
          value.from &&
          isValidDate(value.from) &&
          value.to &&
          isValidDate(value.to)
        ) {
          const fromDate = new Date(value.from);
          const toDate = new Date(value.to);
          const diffInMs = toDate - fromDate;
          const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

          if (diffInDays <= 30) {
            addRangeQuery(attendanceQueryPath[key], value.from, value.to);
          } else {
            console.warn("Date range exceeds 30 days, skipping query.");
          }
        }
        break;

      case "captureApproval":
      case "status":
        break;

      default:
        if (isValidArray(value)) {
          addQuery(captureQueryPath[key], "in", value);
        }
        break;
    }
  });

  return query;
};

export const workforceFilterData = (locations) => [
  {
    key: "type",
    title: "TYPE",
    multiple: true,
    options: ["Employee", "Admin"]
  },
  {
    key: "assignedLocations",
    title: "ASSIGNED LOCATIONS",
    multiple: true,
    search: true,
    searchData: {
      label: "Search PCT / Address"
    },
    options: getLocationList(locations)
  }
];
